import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

/* Shared pieces for the Media page: type filter, scope maths, a
   busy-aware dialog and the one confirm host. */

export type TypeKey = 'all' | 'photos' | 'videos' | 'prints' | 'originals' | 'edited';

export const TYPES: { key: TypeKey; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'photos', label: 'Photos' },
    { key: 'videos', label: 'Videos' },
    { key: 'prints', label: 'Prints' },
    { key: 'originals', label: 'Originals' },
    { key: 'edited', label: 'Edited' },
];

export const isVideo = (m: any) => m?.smileportType === 'processed-video' || String(m?.mimeType ?? '').startsWith('video/');

export function matches(m: any, type: TypeKey) {
    const st = m.smileportType ?? '';
    switch (type) {
        case 'all':
            return true;
        case 'photos':
            return st === 'processed';
        case 'videos':
            return isVideo(m);
        case 'prints':
            return st === 'composite' || st === 'print';
        case 'originals':
            return st === 'original';
        case 'edited':
            return st === 'user-edit' || st === 'edited';
    }
}

export const plural = (n: number, word: string, many = `${word}s`) => `${n.toLocaleString()} ${n === 1 ? word : many}`;

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The hook types anything with "@" as email, everything else as SMS. */
export function destinationError(value: string): string | undefined {
    const v = value.trim();
    if (!v) return 'Enter an email address or a phone number.';
    if (v.includes('@')) return EMAIL_RE.test(v) ? undefined : 'Enter a valid email address.';
    const digits = v.replace(/\D/g, '');
    if (!/^\+?[\d\s().-]+$/.test(v) || digits.length < 7) return 'Enter an email address or a phone number.';
    return undefined;
}

export const errText = (e: any, fallback: string) => {
    const d = e?.response?.data;
    const msg = typeof d === 'string' ? d : d?.error ?? d?.message;
    return typeof msg === 'string' && msg.length < 200 ? msg : fallback;
};

/**
 * Bulk calls address media by share identifier, which covers every file
 * from the same capture (original, print, video…), not only the tiles
 * picked. This spells out the real reach for confirms.
 */
export interface Scope {
    picked: number;
    ids: string[];
    files: number;
    skipped: number;
}

export function scopeOf(rows: any[], all: any[]): Scope {
    const ids = Array.from(new Set(rows.map((m) => m.shareIdentifier).filter(Boolean))) as string[];
    const set = new Set(ids);
    return {
        picked: rows.length,
        ids,
        files: all.filter((m) => m.shareIdentifier && set.has(m.shareIdentifier)).length,
        skipped: rows.filter((m) => !m.shareIdentifier).length,
    };
}

export function scopeNote(s: Scope): string {
    const parts: string[] = [];
    if (s.files > s.picked - s.skipped)
        parts.push(`This applies to all ${plural(s.files, 'file')} in ${s.ids.length === 1 ? 'that capture' : `those ${s.ids.length.toLocaleString()} captures`}, including ones not shown.`);
    if (s.skipped > 0) parts.push(`${plural(s.skipped, 'item')} without a share ID ${s.skipped === 1 ? 'is' : 'are'} skipped.`);
    return parts.join(' ');
}

/* only the top-most overlay answers Escape */
const stack: number[] = [];
let seq = 0;
export function useTopmost(open: boolean) {
    const id = useRef(0);
    if (id.current === 0) id.current = ++seq;
    useEffect(() => {
        if (!open) return;
        const me = id.current;
        stack.push(me);
        return () => {
            const i = stack.lastIndexOf(me);
            if (i >= 0) stack.splice(i, 1);
        };
    }, [open]);
    return () => stack[stack.length - 1] === id.current;
}

/** Outside-click closer for the toolbar popovers. */
export function useDismiss(open: boolean, onClose: () => void) {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) onClose();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);
    return ref;
}

/** The kit dialog, but it can't be dismissed while a request is in flight. */
export function Sheet({
    open,
    title,
    onClose,
    busy,
    wide,
    onSubmit,
    footer,
    children,
}: {
    open: boolean;
    title: string;
    onClose: () => void;
    busy?: boolean;
    wide?: boolean;
    onSubmit?: () => void;
    footer?: React.ReactNode;
    children: React.ReactNode;
}) {
    const isTop = useTopmost(open);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const busyRef = useRef(busy);
    busyRef.current = busy;
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isTop() && !busyRef.current) {
                e.stopPropagation();
                closeRef.current();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    if (!open) return null;
    const body = (
        <>
            <div className="dialog-b">{children}</div>
            {footer && <div className="dialog-f med-dfoot">{footer}</div>}
        </>
    );
    return (
        <div className="psv3-scrim med-scrim" onMouseDown={() => !busy && onClose()}>
            <div
                className={wide ? 'dialog med-dialog wide' : 'dialog med-dialog'}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                aria-busy={busy || undefined}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="dialog-h">{title}</div>
                {onSubmit ? (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (!busy) onSubmit();
                        }}
                    >
                        {body}
                    </form>
                ) : (
                    body
                )}
            </div>
        </div>
    );
}

export interface ConfirmSpec {
    title: string;
    body: React.ReactNode;
    actionLabel: string;
    busyLabel: string;
    danger?: boolean;
    /** resolve a string to show it as the error and stay open */
    run: () => Promise<string | void>;
}

/** One confirm at a time: stays open while running, shows failures inline. */
export function ConfirmHost({ spec, onClose }: { spec: ConfirmSpec | null; onClose: () => void }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const running = useRef(false);
    const mounted = useRef(true);
    useEffect(
        () => () => {
            mounted.current = false;
        },
        [],
    );
    useEffect(() => {
        setError(null);
        setBusy(false);
        running.current = false;
    }, [spec]);

    const go = async () => {
        if (!spec || running.current) return;
        running.current = true;
        setBusy(true);
        setError(null);
        const res = await spec.run().catch((e) => errText(e, 'Something went wrong — nothing changed. Try again.'));
        running.current = false;
        if (!mounted.current) return;
        setBusy(false);
        if (typeof res === 'string') setError(res);
        else onClose();
    };

    return (
        <Sheet
            open={spec != null}
            title={spec?.title ?? ''}
            busy={busy}
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="tb-btn" disabled={busy} onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" className={spec?.danger ? 'tb-new danger' : 'tb-new'} disabled={busy} onClick={go}>
                        {busy ? spec?.busyLabel : spec?.actionLabel}
                    </button>
                </>
            }
        >
            <div className="med-confirm">{spec?.body}</div>
            {error && (
                <p className="field-e med-err" role="alert">
                    {error}
                </p>
            )}
        </Sheet>
    );
}

/* ---- icons ---- */

const svg = (d: React.ReactNode, size = 14) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {d}
    </svg>
);
export const IconHeart = ({ filled }: { filled?: boolean }) => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" aria-hidden="true">
        <path d="M10 16.5s-6-3.6-6-8.1A3.4 3.4 0 0 1 10 6a3.4 3.4 0 0 1 6 2.4c0 4.5-6 8.1-6 8.1z" />
    </svg>
);
export const IconEye = () => svg(<><path d="M2.5 10s2.8-5 7.5-5 7.5 5 7.5 5-2.8 5-7.5 5-7.5-5-7.5-5z" /><circle cx="10" cy="10" r="2.2" /></>);
export const IconEyeOff = () => svg(<><path d="M3 3l14 14M8.3 5.3A7.6 7.6 0 0 1 10 5c4.7 0 7.5 5 7.5 5a12.6 12.6 0 0 1-2.2 2.8M5.4 6.6A12.4 12.4 0 0 0 2.5 10s2.8 5 7.5 5c1 0 1.9-.2 2.7-.5" /></>);
export const IconExpand = () => svg(<path d="M12 3h5v5M8 17H3v-5M17 3l-6 6M3 17l6-6" />);
export const IconCheck = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
        <path d="m5 12 4 4L19 6" />
    </svg>
);
export const IconPlay = () => (
    <svg width="9" height="9" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
        <path d="M2 1l7 4-7 4z" />
    </svg>
);
export const IconFilter = () => svg(<path d="M3 5h14M6 10h8M8.5 15h3" />);
export const IconSliders = () => svg(<><path d="M4 6h8M15 6h1M4 14h2M9 14h7" /><circle cx="13.5" cy="6" r="1.6" /><circle cx="7.5" cy="14" r="1.6" /></>);
export const IconUpload = () => svg(<path d="M10 13V3M6 7l4-4 4 4M4 13v3h12v-3" />);
export const IconDownload = () => svg(<path d="M10 3v10M6 9l4 4 4-4M4 16h12" />);
export const IconHistory = () => svg(<><path d="M3.5 10a6.5 6.5 0 1 0 2-4.7L3.5 7" /><path d="M3.5 3.5V7H7M10 6.5V10l2.5 1.5" /></>);
export const IconPrev = () => svg(<path d="M12.5 4l-6 6 6 6" />, 18);
export const IconNext = () => svg(<path d="M7.5 4l6 6-6 6" />, 18);
export const IconX = () => svg(<path d="M5 5l10 10M15 5L5 15" />, 16);
